// src/features/mech/sheetmetal/questions/MaterialGradeQuestion.jsx
import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Box } from "@mui/material";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlChip from "../../../../shared/components/PerlChip";
import { apiFetch } from "../../../../api/apiClient";

/**
 * MaterialGradeQuestion Component
 *
 * Custom question for Mechanical Sheet Metal: captures the material grade
 * Should be shown after material type is validated and valid
 *
 * @param {string} value - Current material grade value
 * @param {string} materialType - The material type (required for validation)
 * @param {function} onChange - Callback when the grade changes (receives string grade)
 * @param {boolean} [isMaterialValid] - Whether the material type is valid
 * @param {boolean} [isValidatingMaterial] - Whether material validation is in progress
 * @param {function} [onValidationChange] - Callback when validation state changes (receives {isValid, isGradeValid, isValidating})
 * @param {string} [error] - Optional error message
 * @param {string} [success] - Optional success/help message
 * @param {function} [onNextStep] - Optional callback to proceed to next step on Enter key
 * @param {number} [currentStep] - Current step index
 */
const MaterialGradeQuestion = forwardRef(({ value, materialType, onChange, isMaterialValid = false, isValidatingMaterial = false, onValidationChange, error, success, onNextStep, currentStep }, ref) => {
  const [materialGrade, setMaterialGrade] = useState(value || "");
  const [gradeValidationMessage, setGradeValidationMessage] = useState("");
  const [isValidatingGrade, setIsValidatingGrade] = useState(false);
  const [isGradeValid, setIsGradeValid] = useState(true); // Default to true to avoid showing error initially

  // Sync grade state when value prop changes
  useEffect(() => {
    setMaterialGrade(value || "");
  }, [value]);

  // Validate grade via API
  const validateGrade = React.useCallback(async (material, grade) => {
    if (!grade || grade.trim() === "") {
      setGradeValidationMessage("");
      setIsGradeValid(true); // Empty grade is valid (optional field)
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
      return true;
    }

    if (!material || material.trim() === "") {
      return true; // Don't validate grade if material is not set
    }

    setIsValidatingGrade(true);
    if (onValidationChange) {
      onValidationChange({ isValid: false, isGradeValid: false, isValidating: true });
    }

    try {
      const response = await apiFetch("/api/mech/validate-material-type", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          materialType: material,
          materialGrade: grade,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        let message = result.message || "";
        
        const isValid = result.is_grade_valid !== false; // Default to true if undefined/null
        setGradeValidationMessage(message);
        setIsGradeValid(isValid);
        
        if (onValidationChange) {
          onValidationChange({ isValid, isGradeValid: isValid, isValidating: false });
        }
        return isValid;
      } else {
        setGradeValidationMessage("");
        setIsGradeValid(true); // Default to valid on API failure
        if (onValidationChange) {
          onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
        }
        return true;
      }
    } catch (err) {
      console.error("Grade validation error:", err);
      setGradeValidationMessage("");
      setIsGradeValid(true); // Default to valid on API failure
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
      return true;
    } finally {
      setIsValidatingGrade(false);
    }
  }, [onValidationChange]);

  // Reset grade validation state when grade changes
  useEffect(() => {
    if (!materialGrade || materialGrade.trim() === "") {
      setGradeValidationMessage("");
      setIsGradeValid(true);
      if (onValidationChange) {
        onValidationChange({ isValid: true, isGradeValid: true, isValidating: false });
      }
    }
  }, [materialGrade, onValidationChange]);

  const handleMaterialGradeChange = (newGrade) => {
    setMaterialGrade(newGrade);
    onChange(newGrade);
  };

  // Handle Enter key on material grade field
  const handleMaterialGradeKeyDown = (e) => {
    if (e.key === "Enter" && onNextStep && materialGrade && materialGrade.trim() !== "") {
      e.preventDefault();
      onNextStep();
    }
  };

  // Check if material type is stainless steel
  const isStainlessSteel = React.useMemo(() => {
    if (!materialType) return false;
    const materialLower = materialType.toLowerCase().trim();
    return (
      materialLower.includes("stainless") ||
      materialLower.includes("ss ") ||
      materialLower === "ss" ||
      materialLower.startsWith("ss-") ||
      materialLower.includes("stainless steel")
    );
  }, [materialType]);

  // Check if material type is aluminum
  const isAluminum = React.useMemo(() => {
    if (!materialType) return false;
    const materialLower = materialType.toLowerCase().trim();
    return (
      materialLower.includes("aluminium") ||
      materialLower.includes("aluminum") ||
      materialLower.includes("al ") ||
      materialLower === "al" ||
      materialLower.startsWith("al-")
    );
  }, [materialType]);

  // Check if material type is copper
  const isCopper = React.useMemo(() => {
    if (!materialType) return false;
    const materialLower = materialType.toLowerCase().trim();
    return (
      materialLower.includes("copper") ||
      materialLower.includes("cu ") ||
      materialLower === "cu" ||
      materialLower.startsWith("cu-")
    );
  }, [materialType]);

  // Common stainless steel grades for sheet metal
  const stainlessSteelGrades = [
    "304",
    "316",
    "301",
  ];

  // Common aluminum grades for sheet metal
  const aluminumGrades = [


  ];

  // Common copper grades for sheet metal
  const copperGrades = [
 

  ];

  // Handle chip selection
  const handleGradeChipClick = (grade) => {
    handleMaterialGradeChange(grade);
  };

  // Expose validation function for parent to call
  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!materialType || materialType.trim() === "") {
        return true; // Can't validate without material type
      }
      // Validate grade when called explicitly (e.g., from next button)
      return await validateGrade(materialType, materialGrade);
    },
    isValid: () => isGradeValid && !isValidatingGrade,
    isGradeValid: () => isGradeValid,
    isValidating: () => isValidatingGrade,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <PerlLabelMedium sx={{ mb: 1, display: "block" }}>Enter Material Grade:</PerlLabelMedium>

      <PerlTextField
        label="Enter Material Grade"
        placeholder="e.g., Grade 304, Grade 316, etc."
        value={materialGrade}
        onChange={(e) => {
          handleMaterialGradeChange(e.target.value);
        }}
        onKeyDown={handleMaterialGradeKeyDown}
        error={Boolean(error) || (gradeValidationMessage && !isValidatingGrade && !isGradeValid)}
        helperText={
          error || 
          (isValidatingGrade ? "Validating..." : "") ||
          (gradeValidationMessage || "")
        }
        fullWidth
      />

      {/* Show stainless steel grade chips if material is stainless steel and validated */}
      {isStainlessSteel && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common Stainless Steel Grades:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 1,
            }}
          >
            {stainlessSteelGrades.map((grade) => (
              <PerlChip
                key={grade}
                label={grade}
                selected={materialGrade === grade}
                onClick={() => handleGradeChipClick(grade)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Show aluminum grade chips if material is aluminum and validated */}
      {isAluminum && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common Aluminum Grades:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 1,
            }}
          >
            {aluminumGrades.map((grade) => (
              <PerlChip
                key={grade}
                label={grade}
                selected={materialGrade === grade}
                onClick={() => handleGradeChipClick(grade)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Show copper grade chips if material is copper and validated */}
      {isCopper && isMaterialValid && !isValidatingMaterial && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <PerlLabelMedium sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
            Common Copper Grades:
          </PerlLabelMedium>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 1,
            }}
          >
            {copperGrades.map((grade) => (
              <PerlChip
                key={grade}
                label={grade}
                selected={materialGrade === grade}
                onClick={() => handleGradeChipClick(grade)}
              />
            ))}
          </Box>
        </Box>
      )}

      {success && (
        <PerlLabelMedium sx={{ mt: 1, color: "success.main" }}>{success}</PerlLabelMedium>
      )}
    </Box>
  );
});

MaterialGradeQuestion.displayName = "MaterialGradeQuestion";

export default MaterialGradeQuestion;

